import type * as React from "react";
import { useEffect, useRef, useState } from "react";

import { flexRender, type Table as TanstackTable } from "@tanstack/react-table";

import { DataTablePagination } from "@/components/data-table/data-table-pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getCommonPinningStyles } from "@/lib/data-table";
import { cn } from "@/lib/utils";
import "./data-table.css";

interface DataTableProps<TData> extends React.ComponentProps<"div"> {
  table: TanstackTable<TData>;
  actionBar?: React.ReactNode;
  showSelectedRowCount?: boolean;
  totalRecords?: number | null;
  pagination?: boolean;
  emptyState?: React.ReactNode;
}

export function DataTable<TData>({
  table,
  actionBar,
  children,
  className,
  showSelectedRowCount = false,
  totalRecords,
  pagination = true,
  emptyState,
  ...props
}: DataTableProps<TData>) {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);

  // Check if table is horizontally scrollable
  useEffect(() => {
    const checkScrollable = () => {
      if (tableContainerRef.current) {
        const container = tableContainerRef.current;
        const tableElement = container.querySelector("table");

        if (tableElement) {
          const containerWidth = container.clientWidth;
          const tableWidth = tableElement.scrollWidth;
          const needsScroll = tableWidth > containerWidth;
          setIsScrollable(needsScroll);
        }
      }
    };

    // Check after DOM updates
    const timeoutId = setTimeout(checkScrollable, 100);

    // Check on resize
    window.addEventListener("resize", checkScrollable);

    // Use mutation observer to detect table content changes
    const observer = new MutationObserver(checkScrollable);
    if (tableContainerRef.current) {
      observer.observe(tableContainerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style", "class"],
      });
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", checkScrollable);
      observer.disconnect();
    };
  }, []);

  return (
    <div className={cn("flex w-full flex-col gap-2.5", className)} {...props}>
      {children}
      <div
        ref={tableContainerRef}
        className={cn(
          "overflow-auto rounded-md border",
          isScrollable && "data-table-scrollable",
        )}
      >
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    className={cn({
                      "pin-left": header.column.getIsPinned() === "left",
                      "pin-right": header.column.getIsPinned() === "right",
                    })}
                    style={{
                      ...getCommonPinningStyles({
                        column: header.column,
                        isOdd: false,
                      }),
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row, rowIndex) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn({
                        "pin-left": cell.column.getIsPinned() === "left",
                        "pin-right": cell.column.getIsPinned() === "right",
                      })}
                      style={{
                        ...getCommonPinningStyles({
                          column: cell.column,
                          isOdd: rowIndex % 2 !== 0,
                        }),
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className="h-24 text-center"
                >
                  {emptyState || "No results."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {pagination && (
        <div className="flex flex-col gap-2.5">
          <DataTablePagination
            totalRecords={totalRecords}
            table={table}
            showSelectedRowCount={showSelectedRowCount}
          />
          {actionBar &&
            table.getFilteredSelectedRowModel().rows.length > 0 &&
            actionBar}
        </div>
      )}
    </div>
  );
}
